import React, { useContext } from 'react'
import { FieldRenderProps } from 'react-final-form'
import { fieldModel } from '../../fields/types/fieldsModel'
import Fab from '../../fab/Fab'
import { WHITE } from '../../../theme'
import styles from '../../../styles'
import { ResponsiveContext } from '../../../utils/context/ResponsiveContext'
import { makeStyles } from 'tss-react/mui'
import {
	biensTypeAppartement,
	biensTypeParking,
	champsMenuRechercheEnum,
	criteresAvancesAppartement,
	prestationsEnum, rechercheAvanceeEnum,
	surfaceEnum
} from '../../../modules/resultats-recherche/enums/champsMenuRecherche'
import { RechercheContext } from '../../../modules/resultats-recherche/utils/RechercheContext'
import { Grid } from '@mui/material'

export const useStyles = makeStyles<{ isLargeScreen: boolean }>()(
	(theme, { isLargeScreen }) => ({
		labelRecherche: {
			...styles(theme).text21,
			fontSize: 18,
			lineHeight: '25px',
			margin: 0,
			marginBottom: 20
		},
		fabStyle: {
			width: '100%',
			borderRadius: '23px !important',
			padding: '15px 20px',
			background: `${WHITE} !important`,
			boxShadow: 'none !important',
			border: '1px solid #D9D9D9 !important',
			...styles(theme).text24,
			fontSize: 14,
			lineHeight: '24px',
			textTransform: 'inherit',

			'&:hover': {
				background: isLargeScreen ? `${theme.palette.primary.main} !important` : 'inherit',
				color: isLargeScreen ? `${WHITE} !important` : 'inherit',
				border: isLargeScreen ? 'none !important' : 'inherit',

				// Pour les SVG où la couleur est sur un "fill", on modifie la couleur de ce fill
				'& .prestation_cls_fill': {
					fill: isLargeScreen ? WHITE : 'inherit'
				},
				// Pour les SVG où la couleur est sur un "stroke" (et donc le fill est "none"), on modifie la couleur de ce stroke
				'& [class*=prestation_cls_fill_none]': {
					stroke: isLargeScreen ? WHITE : 'inherit'
				}
			},
			'&:disabled': {
				opacity: '0.7 !important'
			}
		},
		fabLocation: {
			paddingBottom: 5
		},
		selectedStyle: {
			background: `${theme.palette.primary.main} !important`,
			color: WHITE,
			border: 'none !important',
			// Pour les SVG où la couleur est sur un "fill", on modifie la couleur de ce fill
			'& .prestation_cls_fill': {
				fill: WHITE
			},
			// Pour les SVG où la couleur est sur un "stroke" (et donc le fill est "none"), on modifie la couleur de ce stroke
			'& [class*=prestation_cls_fill_none]': {
				stroke: WHITE
			}
		},
		fabTypeBien: {
			'& button': {
				padding: 0
			}
		},
		fabPrestations: {
			height: 'fit-content'
		}
	})
)

interface AcceptingProps {
	id: string
	label: string
	options: fieldModel[]
}

type FieldsSelectionMultipleRechercheFormProps =
	AcceptingProps
	& FieldRenderProps<string[]>

const FieldsSelectionMultipleRechercheForm: React.FC<FieldsSelectionMultipleRechercheFormProps> = (
	{
		input,
		label,
		id,
		options
	}
) => {
	const { isLargeScreen } = useContext(ResponsiveContext)
	const { classes, cx } = useStyles({ isLargeScreen })
	const estLocation = input.name === champsMenuRechercheEnum.location
	const estTypeBien = input.name === champsMenuRechercheEnum.typeBien
	const { filtres } = useContext(RechercheContext)

	const gestionPrestation = (valeurOption: string) => {
		// Si on a sélectionné la cuisine ouverte alors que la cuisine séparée est déjà sélectionnée, on l'enlève du champ
		if (valeurOption === prestationsEnum.cuisineOuverte && input.value.includes(prestationsEnum.cuisineSeparee)) {
			input.onChange([...input.value.filter((value) => value !== prestationsEnum.cuisineSeparee), valeurOption])
		}
		// On fait de même si cuisine séparée est sélectionnée
		else if (valeurOption === prestationsEnum.cuisineSeparee && input.value.includes(prestationsEnum.cuisineOuverte)) {
			input.onChange([...input.value.filter((value) => value !== prestationsEnum.cuisineOuverte), valeurOption])
		}
		// Sinon, fonctionnement normal
		else {
			input.onChange([...input.value, valeurOption])
		}
	}

	const auClickFab = (valeurOption: string) => {
		// Si l'option est déjà sélectionnée, on la retire du form
		if (input.value.includes(valeurOption)) {
			input.onChange(input.value.filter((value) => value !== valeurOption))
		} else {
			// Si on est dans le cas d'un changement relatif aux prestations de la recherche avancée
			if (input.name === `${champsMenuRechercheEnum.rechercheAvancee}.${rechercheAvanceeEnum.prestation}`) {
				gestionPrestation(valeurOption)
			} else {
				input.onChange([...input.value, valeurOption])
			}
		}
	}

	const gestionChampDisabledSelonCritere = (valeurOption: string) => {
		if (biensTypeAppartement.includes(valeurOption) && input.value.some((value) => biensTypeParking.includes(value))) {
			return true
		} else if (biensTypeParking.includes(valeurOption) && input.value.some((value) => biensTypeAppartement.includes(value))) {
			return true
		} else if (input.name.split('.').includes(rechercheAvanceeEnum.parking) && filtres[champsMenuRechercheEnum.typeBien].some((type) => biensTypeAppartement.includes(type))) {
			return true
		} else if (input.name.split('.').some((valeur) => criteresAvancesAppartement.includes(valeur)) && filtres[champsMenuRechercheEnum.typeBien].some((type) => !biensTypeAppartement.includes(type))) {
			return true
		} else if ((biensTypeAppartement.includes(valeurOption) || (input.name.split('.').includes(champsMenuRechercheEnum.rechercheAvancee) && !input.name.split('.').includes(rechercheAvanceeEnum.parking))) && filtres[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.parking].length) {
			return true
		} else if ((biensTypeParking.includes(valeurOption) || (input.name.split('.').includes(champsMenuRechercheEnum.rechercheAvancee) && input.name.split('.').includes(rechercheAvanceeEnum.parking))) && (
			(filtres[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.surface] && filtres[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.surface][surfaceEnum.max])
			|| (filtres[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.surface] && filtres[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.surface][surfaceEnum.min])
			|| filtres[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.dernierEtage]
			|| filtres[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.meuble]
			|| filtres[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.combienDeChambre].length
			|| filtres[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.prestation].length
			|| filtres[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.quelEtage].length
			|| filtres[champsMenuRechercheEnum.rechercheAvancee][rechercheAvanceeEnum.combienDeChambre].length
		)) {
			return true
		}
	}

	return (
		<Grid container item xs={12}>
			<Grid container item xs={12}>
				<p className={classes.labelRecherche}>
					{label}
				</p>
			</Grid>

			<Grid
				container
				item
				xs={12}
				spacing={estLocation ? 0 : 1}
				id={id}
			>
				{
					options.map(
						(option) => (
							// Les fabs sont full size si on est sur le choix de la ville
							<Grid
								item
								xs={estLocation ? 12 : estTypeBien ? 6 : 'auto'}
								className={
									cx(
										{
											[classes.fabLocation]: estLocation,
											[classes.fabTypeBien]: estTypeBien
										}
									)
								}
								key={option.value}
							>
								<Fab
									ariaLabel={option.label}
									onClick={() => auClickFab(option.value)}
									isSelected={input.value.includes(option.value)}
									label={option.label}
									disabled={gestionChampDisabledSelonCritere(option.value)}
									fabStyle={
										cx(
											classes.fabStyle,
											{
												[classes.fabPrestations]: input.name === `${champsMenuRechercheEnum.rechercheAvancee}.${rechercheAvanceeEnum.prestation}`,
												[classes.selectedStyle]: input.value.includes(option.value)
											}
										)
									}
								/>
							</Grid>
						)
					)
				}
			</Grid>
		</Grid>
	)
}

export default FieldsSelectionMultipleRechercheForm